import { parseISO } from 'date-fns';
import difference from 'lodash-es/difference';
import { Paginated } from '../../../core/common/entities/mediahaven-api/paginated.entity';
import { GroupId } from '../../../core/groups/entities/group.entity';
import { TaskKey } from '../../../core/tasks/entities/task-key.type';
import { Task } from '../../../core/tasks/entities/task.entity';
import { ZoneId } from '../../../core/zones/entities/zone.entity';
import { createValidationResponse, ValidationResponse } from '../../api/entities/validation-response.entity';
import { get, set } from '../helpers/deep-get-set.helper';
import { ContextReason } from './metadata/context-reason.entity';
import {
    ContextMetadata,
    DeleteStatus,
    Family,
    FragmentId,
    Identifier,
    MediaObjectId,
    MediaType,
    Metadata,
    MH2MediaType,
    MH2Types,
    Permissions,
    RecordId,
    RecordPhase,
    RecordStatus,
    RecordType,
    RecordTypeStructure,
    ReferenceCodes,
    ReferenceTitles,
    UserContact,
    VersioningStatus
} from './metadata/metadata.entity';
import { PartialRecord } from './partial-record.class';

export type ApiFragmentList = Paginated<Metadata>;

export enum Mh2Entity {
    'Collection' = 'collection',
    'Item' = 'item',
    'Generic' = 'generic',
    'ThesaurusSuggestion' = 'thesaurus-suggestion',
    'UserSuggestion' = 'user-suggestion'
}

export class ApiFragment extends PartialRecord {
    declare public readonly metadata: Metadata;

    constructor(metadata: Metadata) {
        super(metadata, false);
    }

    getTitle(): string {
        return this.get(Family.Descriptive, 'Title');
    }

    getUserContact(): UserContact {
        return this.get(Family.RecordInformation, 'UserContact');
    }

    getFragmentId(): FragmentId {
        return this.get(Family.Internal, 'FragmentId');
    }

    getMediaObjectId(): MediaObjectId {
        return this.get(Family.Internal, 'MediaObjectId');
    }

    getRecordId(): RecordId {
        return this.get(Family.Internal, 'RecordId');
    }

    getIdentifier(): Identifier {
        return this.getRecordId();
    }

    getOriginalRecordId(): RecordId | null {
        return this.get(Family.Structural, 'OriginalRecordId');
    }

    getLogicalDeletionDate(): Date | null {
        const logicalDeletionDate: string | null = this.get(Family.Administrative, 'LogicalDeletionDate');
        if (!logicalDeletionDate) {
            return null;
        }
        return parseISO(logicalDeletionDate);
    }

    getArchiveDate(): Date | null {
        const archiveDate: string | null = this.get(Family.Administrative, 'ArchiveDate');
        if (!archiveDate) {
            return null;
        }
        return parseISO(archiveDate);
    }

    getLastModifiedDate(): Date {
        const lastModifiedDate: string = this.get(Family.Administrative, 'LastModifiedDate');

        return parseISO(lastModifiedDate);
    }

    getDestructionDate(): Date | null {
        const destructionDate: string =
            this.get(Family.Timers, 'Destruction')?.EffectiveDate ??
            this.get('Governance', 'AutomaticDestruction')?.EffectiveDate;

        if (!destructionDate) {
            return null;
        }

        return parseISO(destructionDate);
    }

    getThumbnailUrl(): string {
        if (this.getRecordStatus() === RecordStatus.Destructed) {
            return 'assets/images/placeholders/no-preview-thumb.svg';
        }

        const browseStatus: string = this.get(Family.Internal, 'BrowseStatus');

        switch (browseStatus) {
            case 'completed':
                return this.get(Family.Internal, 'PathToKeyframeThumb');
            case 'in_progress':
            case 'failed':
            case 'no_browse':
            default:
                return 'assets/images/placeholders/no-preview-thumb.svg';
        }
    }

    getMh2Entity(): Mh2Entity {
        if (this.isThesaurusSuggestion()) {
            return Mh2Entity.ThesaurusSuggestion;
        }
        if (this.isUserSuggestion()) {
            return Mh2Entity.UserSuggestion;
        }
        if (this.isNewCollection()) {
            return Mh2Entity.Collection;
        }
        if (this.getRecordType() === RecordType.Record) {
            return Mh2Entity.Item;
        }
        return Mh2Entity.Generic;
    }

    getProfileIds(): string[] {
        return this.get(Family.Context, 'Profiles') || []; // Profiles can be null instead of empty array, this makes it consistent
    }

    getClassificationProfileIdsOfRecordType(recordType: string | RecordType): string[] {
        return this.get2(Family.Internal, 'Profiles', recordType) || [];
    }

    getClassificationTitle(): string | null {
        const referenceTitles: ReferenceTitles = this.get(Family.Structural, 'ReferenceTitles');
        return referenceTitles && referenceTitles.Classification ? referenceTitles.Classification[0] : null;
    }

    getDirectParentTitle(): string | null {
        const parentrecordId = this.getParentRecordId()?.toString() || null;
        const referenceTitles: ReferenceTitles = this.get(Family.Structural, 'ReferenceTitles');

        if (parentrecordId) {
            const matchingRecordType = this.getDirectParentRecordType();

            if (matchingRecordType && referenceTitles.hasOwnProperty(matchingRecordType)) {
                const matchingTitles = referenceTitles[matchingRecordType];

                if (matchingTitles && matchingTitles.length > 0) {
                    // MED-4003 It's possible for mails to have multiple parents, pick the last in the list
                    return Array.isArray(matchingTitles) ? matchingTitles[matchingTitles.length - 1] : matchingTitles;
                }
            }
        }

        return null;
    }

    getDirectParentRecordType(): RecordType | null {
        const parentrecordId = this.getParentRecordId()?.toString() || null;
        const referenceCodes: ReferenceTitles = this.get(Family.Structural, 'ReferenceCodes');

        return parentrecordId
            ? ((Object.entries(referenceCodes).find(([key, values]: [string, string[]]) =>
                  values?.includes(parentrecordId)
              )?.[0] || null) as RecordType)
            : null;
    }

    getDirectParentRefCode(): MediaObjectId | undefined {
        const referenceCodes: ReferenceCodes = this.get(Family.Structural, 'ReferenceCodes');
        const parentId: MediaObjectId | null = this.getParentRecordId() as MediaObjectId | null;

        if (!parentId) {
            return undefined;
        }

        const parentKey: RecordType | undefined = (Object.keys(referenceCodes) as RecordType[]).find(
            key =>
                referenceCodes[key]!.length === 1 &&
                referenceCodes[key]![0] === parentId &&
                key !== RecordType.Classification
        );

        return parentKey ? referenceCodes[parentKey]![0] : undefined;
    }

    getCandidateParentId(): FragmentId | null {
        const candidateCollectionIds: FragmentId[] = this.getRelationFragmentIdsOfType('IsCandidateChildOf');

        if (candidateCollectionIds.length > 1) {
            console.error('ApiFragment:: Record can not be candidate child of multiple items');
        }

        return candidateCollectionIds.length > 0 ? candidateCollectionIds[0] : null;
    }

    isTopLevelRecord(): boolean {
        const parentType: string | null = this.getDirectParentRecordType();
        return !parentType || parentType === RecordType.Classification;
    }

    getZoneId(): ZoneId | null {
        return (this.get(Family.RightsManagement, 'Zone')?.Id as ZoneId) ?? null;
    }

    getMediaHavenType(): MediaType {
        return this.get(Family.Administrative, 'Type');
    }

    getExtendedType(): MH2MediaType | RecordType {
        const recordType: RecordType = this.getRecordType();
        if (recordType.endsWith('.Fragment')) {
            return MH2Types.Fragment;
        }

        if (recordType === RecordType.Record) {
            return this.getType();
        }

        return this.getRecordType();
    }

    hasPendingTask(): boolean {
        const tasks: Task[] = this.getTasks();
        return tasks.length > 0;
    }

    getTasks(): Task[] {
        const tasks: Task[] | undefined = this.get(Family.Workflow, 'Tasks')?.Task;
        if (!tasks) {
            return [];
        }

        // When publishing using batch, submitted items can be published without closing the task
        if (this.isPublished()) {
            return tasks.filter(
                task => ![TaskKey.CorrectMedia, TaskKey.EvaluateMedia].includes(task.TypeId as TaskKey)
            );
        }
        return tasks;
    }

    getSelectionItemsIds(): FragmentId[] {
        const fragmentIds: FragmentId[] = this.getRelationFragmentIdsOfType('Contains');
        return fragmentIds;
    }

    getRelationFragmentIdsOfType(relationType: string): FragmentId[] {
        const fragmentIds: FragmentId[] | null = get(this.metadata, Family.Structural, 'Relations', relationType);
        return fragmentIds ? fragmentIds : [];
    }

    getRelationTypes(): string[] {
        const relations: Record<string, FragmentId[] | null> = get(this.metadata, Family.Structural, 'Relations');
        return Object.keys(relations);
    }

    getMainFragmentId(): FragmentId | null {
        return get(this.metadata, Family.Structural, 'MainFragment');
    }

    isMainFragment(): boolean {
        return !get(this.metadata, Family.Structural, 'MainFragment');
    }

    getChildFragmentsIds(): FragmentId[] {
        return get(this.metadata, Family.Structural, 'Fragments', 'Fragment') || [];
    }

    getDeleteStatus(): DeleteStatus {
        return this.get(Family.Administrative, 'DeleteStatus');
    }

    isImage(): boolean {
        return this.getType() === MediaType.image;
    }

    getKeywords(): string[] {
        return this.get(Family.Descriptive, 'Keywords').Keyword;
    }

    isPersonalSelection(): boolean {
        return this.get(Family.Administrative, 'RecordType') === RecordType.PersonalSelection;
    }

    isGenericSelection(): boolean {
        return this.get(Family.Administrative, 'RecordType') === RecordType.GenericSelection;
    }

    isSelection(): boolean {
        return (
            this.isPersonalSelection() ||
            this.isGenericSelection() ||
            this.get(Family.Administrative, 'RecordType') === RecordType.Selection
        );
    }

    hasChildren(): boolean {
        return this.get(Family.Structural, 'HasChildren');
    }

    hasKeyFrames(): boolean {
        return this.get(Family.Internal, 'HasKeyframes');
    }

    hasParent(): boolean {
        const parents: string[] = Object.keys(this.get(Family.Structural, 'ReferenceCodes') ?? {});

        return parents.length > 1 || (parents.length === 1 && parents[0] !== RecordType.Classification);
    }

    hasCandidateChildren(): boolean {
        const candidateChildren: FragmentId[] = this.get(Family.Structural, 'Relations')?.IsCandidateParentOf ?? [];
        return candidateChildren.length > 0;
    }

    isCandidateChild(): boolean {
        return this.getRelationFragmentIdsOfType('IsCandidateChildOf').length > 0;
    }

    isCandidateParent(): boolean {
        return this.getRelationFragmentIdsOfType('IsCandidateParentOf').length > 0;
    }

    isParentOf(record: ApiFragment): boolean {
        return record.isChildOf(this);
    }

    isChildOf(record: ApiFragment): boolean {
        return this.getParentRecordId() === record.getRecordId();
    }

    isInSelection(selectionId: FragmentId): boolean {
        const fragmentIds: FragmentId[] | null = get(this.metadata, Family.Structural, 'Relations', 'ContainedBy');

        return fragmentIds != null && fragmentIds.includes(selectionId);
    }

    isDeleted(): boolean {
        const deleteStatus: DeleteStatus = this.getDeleteStatus();
        return [DeleteStatus.PermanentlyDeleted, DeleteStatus.LogicallyDeleted].includes(deleteStatus);
    }

    selectionContains(fragmentId: FragmentId | FragmentId[]): boolean {
        const fragmentIds: FragmentId[] | null = get(this.metadata, Family.Structural, 'Relations', 'Contains');
        const toCheck: FragmentId[] = Array.isArray(fragmentId) ? fragmentId : [fragmentId];

        return fragmentIds != null && difference(toCheck, fragmentIds).length === 0;
    }

    getVersioningStatus(): VersioningStatus {
        return this.get(Family.Structural, 'Versioning').Status;
    }

    isPlayableMedia(): boolean {
        if (this.getRecordStatus() === RecordStatus.Destructed) {
            return false;
        }
        const playableMediaTypes: MH2MediaType[] = [
            MediaType.audio,
            MediaType.audiofragment,
            MediaType.video,
            MediaType.videofragment
        ];
        return playableMediaTypes.includes(this.getType());
    }

    getMediaUrl(): string | null {
        if (this.getRecordStatus() === RecordStatus.Destructed) {
            return null;
        }
        const structure: RecordTypeStructure = this.getRecordStructure();

        switch (structure) {
            case RecordTypeStructure.Data:
            case RecordTypeStructure.Representation:
            case RecordTypeStructure.DataFlat:
                return this.get('Internal', 'PathToPreview');
            default:
                return null;
        }
    }

    getHeight(): number | null {
        return this.get(Family.Technical, 'Height');
    }

    getWidth(): number | null {
        return this.get(Family.Technical, 'Width');
    }

    getValidation(): string[] {
        return this.get(Family.Dynamic, 'Mh2InvalidFields')?.Mh2DottedKey ?? [];
    }

    private getBrowses(container: string): Record<string, string>[] | null {
        const topBrowse: any | null = this.get(Family.Internal, 'Browses');

        if (!(topBrowse && topBrowse.Browse)) {
            return null;
        }

        const browses: Record<string, string>[] | null = topBrowse.Browse.filter((browse: Record<string, string>) => {
            return browse.Container === container;
        });

        if (browses === null || !Array.isArray(browses) || browses.length === 0) {
            return null;
        }
        return browses;
    }

    public getSubtitleUrl(): string | null {
        const browses: Record<string, string>[] | null = this.getBrowses('mp4');

        if (browses === null || !Array.isArray(browses) || browses.length === 0) {
            return null;
        }

        const url: string = browses[0].BaseUrl + '/browse.srt';

        return url;
    }

    public get<F extends keyof NonNullable<Metadata>, P extends keyof NonNullable<NonNullable<Metadata>[F]>>(
        family: F,
        property: P
    ): NonNullable<NonNullable<Metadata>[F]>[P] {
        return get(this.metadata, family, property);
    }

    public get2<
        F extends keyof NonNullable<Metadata>,
        P extends keyof NonNullable<NonNullable<Metadata>[F]>,
        Q extends keyof NonNullable<NonNullable<NonNullable<Metadata>[F]>[P]>
    >(family: F, property: P, subproperty: Q): NonNullable<NonNullable<NonNullable<Metadata>[F]>[P]>[Q] {
        return get(this.metadata, family, property, subproperty);
    }

    public set<F extends keyof NonNullable<Metadata>, P extends keyof NonNullable<NonNullable<Metadata>[F]>>(
        family: F,
        property: P,
        value: NonNullable<NonNullable<Metadata>[F]>[P]
    ): void {
        return set(this.metadata, value, family, property);
    }

    public setIfNotNull<F extends keyof NonNullable<Metadata>, P extends keyof NonNullable<NonNullable<Metadata>[F]>>(
        family: F,
        property: P,
        value: NonNullable<NonNullable<Metadata>[F]>[P] | undefined
    ): void {
        if (typeof value !== 'undefined' && value !== null) {
            return set(this.metadata, value, family, property);
        }
    }

    public set2<
        F extends keyof NonNullable<Metadata>,
        P extends keyof NonNullable<NonNullable<Metadata>[F]>,
        Q extends keyof NonNullable<NonNullable<NonNullable<Metadata>[F]>[P]>
    >(family: F, property: P, subproperty: Q, value: NonNullable<NonNullable<NonNullable<Metadata>[F]>[P]>[Q]): void {
        return set(this.metadata, value, family, property, subproperty);
    }

    hasEditableStatus(): boolean {
        return this.get(Family.Context, 'HasEditableStatus');
    }

    canBeEdited(): boolean {
        const canEdit: boolean = this.hasEditRights() && this.hasEditableStatus() && !this.isDeleted();
        return canEdit;
    }

    hasDeleteRights(): boolean {
        const canDelete: boolean = this.get(Family.Context, 'IsDeletable');
        return canDelete;
    }

    hasSubmitRights(): boolean {
        const submitRights: boolean = this.get(Family.Context, 'IsSubmittable');
        return submitRights;
    }

    canBeSubmitted(): boolean {
        const canSubmit: boolean = this.hasSubmitRights() && this.get(Family.Context, 'HasSubmittableStatus');
        return canSubmit;
    }

    isPublishable(): boolean {
        const isPublishable: boolean = this.get(Family.Context, 'IsPublishable');
        return isPublishable;
    }

    hasExportRights(): boolean {
        const exportRights: boolean = this.get(Family.Context, 'IsExportable');
        return exportRights;
    }

    isPublic(): boolean {
        const isPublic: boolean = this.get(Family.Context, 'IsPublic');
        return isPublic;
    }

    getContextReasonCode(property: string): string | null {
        // TODO: Add Reasons and ContextReasons to Metadata interface in Angular Components

        const reasons: ContextReason[] | null = this.get2(
            Family.Context,
            'Reasons' as keyof ContextMetadata,
            property as never
        ) as ContextReason[];

        return reasons ? reasons[0].Code : null;
    }

    hasValidModel(): boolean {
        const validation: ValidationResponse | null = createValidationResponse(this.getValidation());
        const valid: boolean = validation ? validation.Valid : false;
        return valid;
    }

    isPublished(): boolean {
        return this.getRecordPhase() === RecordPhase.Published;
    }

    isArchived(): boolean {
        return this.getRecordPhase() === RecordPhase.Archived;
    }

    isCompleted(): boolean {
        const incompleteStatuses: RecordStatus[] = [RecordStatus.Processing, RecordStatus.Rejected];
        return !incompleteStatuses.includes(this.getRecordStatus());
    }

    getPermissions<T extends keyof Permissions>(key: T): GroupId[] {
        return this.get2(Family.RightsManagement, 'Permissions', key) as GroupId[];
    }

    asPartialRecord(): PartialRecord {
        return new PartialRecord(this.metadata);
    }

    getHumanReadableFilesize(): string {
        const sizes: string[] = ['B', 'KB', 'MB', 'GB'];
        let fileSize: number = this.metadata.Technical.FileSize;
        let i: number = 0;

        while (i < sizes.length) {
            if (fileSize / 1000 <= 1) {
                return `${Math.round(fileSize * 100) / 100} ${sizes[i]}`;
            }

            i++;
            fileSize /= 1000;
        }
        return `${Math.round(fileSize * 100000) / 100} ${sizes[i - 1]}`;
    }
}

export function isApiFragment(object: any): boolean {
    return object instanceof ApiFragment;
}
